import React from "react"

import styled from "styled-components"

import { Box, Typography } from "components/common"
import Perks from "jewlr/components/perks"

import { Paragraph, Section } from "./jewlr/components"

const Title = styled(Box)`
  font-size: 18px;
  font-weight: 700;
  text-align: center;
  text-transform: uppercase;
`
const ColorBox = styled(Box)`
  background-color: ${({ theme }) => theme.colors.primaryColors.lighter[0]};
  display: flex;
  flex-direction: column;
  font-size: 16px;
  font-weight: 500;
  justify-content: center;
  line-height: 1.4;
  text-align: center;
`
const Content = styled(Box)`
  font-size: 16px;
  font-weight: 500;
  line-height: 1.4;
  text-align: center;
`
const SmallContent = styled(Box)`
  font-size: 12px;
  font-weight: 500;
  text-align: center;
`

const ContentBox = styled(Box)`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  height: 100%;
  justify-content: center;
`

const BoldContent = styled(Paragraph).attrs({
  as: "span",
  fontSize: "16px",
  fontWeight: "700",
  lightHeight: "1.4",
})``
const SmallBoldContent = styled(Paragraph).attrs({
  as: "span",
  fontSize: "12px",
  fontWeight: "700",
})``

const TitleBorder = styled(Typography)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.black};
  display: block;
  margin: auto;
  width: 55px;
`
const TitleBorderSidebar = styled(Typography)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.white};
  display: block;
  margin: auto;
  width: 55px;
`

const ContentBorder = styled(Typography)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.primary};
  display: block;
`
const BoldTitle = styled(Typography)`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 16px;
  font-weight: 900;
  text-align: center;
`

const SectionPicBackground = styled(Section)`
  background-image: url(${require("images/info/hallmark/mobile/hallmark_mobile.jpg")});
  background-position: center center;
  background-size: cover;
  height: 640px;
  ${({ theme }) => theme.mediaQueries.tablet`
        background-image: url(${require("images/info/hallmark/hallmarking_tablet.jpg")});
        background-size: cover;
        background-position: center center;
        height:100%;
      `};
  margin-bottom: 0px;
`

const LeftBar = styled(Box)`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
`

const RightBar = styled(Box)`
  border-bottom: solid 1px ${({ theme }) => theme.colors.primary};
  border-top: solid 1px ${({ theme }) => theme.colors.primary};
  height: 100%;
  text-align: center;
`
const Link = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 16px;
  font-weight: 500;
  line-height: 1.4;
`
const Hallmark = () => (
  <Box contain mb={8}>
    <SectionPicBackground gutter={{ tablet: 1 }} px={3} py={3}>
      <Box flex="1"></Box>
      <Box flex="1" my={{ _: 1, tablet: 3 }}>
        <Title px={{ _: 4, tablet: 7 }}>
          <Typography as="span" fontSize="24px">
            A HALLMARK IS YOUR GUARANTEE.
          </Typography>
        </Title>
        <TitleBorder pt={{ _: 2, tablet: 3 }} />
        <Content pt={{ _: 2, tablet: 3 }} px={{ _: 2, tablet: 5 }}>
          Since the 1400&apos;s, the official{" "}
          <Typography as="span" fontSize="16px" fontWeight="700">
            Edinburgh Assay Office (EAO)
          </Typography>{" "}
          has been testing and hallmarking precious metals to ensure their
          fineness and quality. Every {sl.config.site_title} piece is carefully
          inspected by the EAO to guarantee our customers are getting nothing
          but the very best.
        </Content>
      </Box>
    </SectionPicBackground>

    <Section gutter={{ tablet: 1 }} mt={1}>
      <ColorBox flex={{ tablet: "1" }} mb={{ _: 1, tablet: 0 }} py={4}>
        <Title mb={1}>WHAT ARE HALLMARKS? </Title>
        <Content px={{ _: 4, tablet: 5 }}>
          Hallmarks are a series of marks stamped on jewellery and other items
          made of precious metals. Hallmarks can only be applied by an
          independent Government authorised Assay Office. The Assay Office must
          test each item before applying the Hallmark. A Hallmark guarantees the
          precious metal content of the alloy used to make the jewellery in
          question. In the UK, Hallmarking has taken place since the 1300s.
        </Content>
        <Content mt={2} px={6}>
          At {sl.config.site_title}, we take pride in the quality and
          craftsmanship of our jewellery, and want our customers to rest assured
          that their personalised jewellery is made using only the finest
          materials.
        </Content>
      </ColorBox>
      <Box flex="1">
        <picture>
          <source
            media="(max-width: 767px)"
            sizes="100vw"
            srcSet={`
            ${require("images/info/hallmark/mobile/image1_mobile.jpg")} 767w
          `}
          />

          <source
            media="(min-width: 768px)"
            sizes="100vw"
            srcSet={`
            ${require("images/info/hallmark/2x/image1_desktop.jpg")} 1045w,
            ${require("images/info/hallmark/2x/image1_desktop_rev.jpg")} 2090w
          `}
          />
          <Box
            as="img"
            height="100%"
            src={require("images/info/hallmark/image1_tablet.jpg")}
            width="100%"
          />
        </picture>
      </Box>
    </Section>
    <Box px={4} py={5}>
      <Title mb={1}>THE REASON FOR USING HALLMARKS</Title>
      <Content>
        Gold and sterling silver are two of the most popular precious metals
        used to make jewellery. In their purest form these metals are too soft
        and must be mixed with other metals, known as alloys, for added strength
        and durability. This ensures that the jewellery will be able to hold its
        shape and withstand a lifetime of wear. A Hallmark guarantees that the
        percentage of precious metal within the alloy{" "}
        <span className="nowrap">is as advertised.</span>
      </Content>
      <Content mt={2}>
        Due to the high cost and value of these precious metals, hallmarking is
        a legal requirement in the UK for precious metals over a certain weight.
      </Content>
    </Box>

    <Section>
      <LeftBar
        flex={{ _: 1, tablet: "1 1 auto" }}
        py={3}
        width={{ _: "100%", tablet: "30%" }}
      >
        <Box mx={3}>
          <Typography color="white" fontSize="26px" italic textAlign="center">
            The Testing Process
          </Typography>
          <TitleBorderSidebar pt={1} />
          <Content mt={2}>
            When an item is received by a UK assay office, it will undergo three
            processes to test the purity of the metal:
          </Content>
          <Title color={"white"} mb={2} mt={3}>
            SAMPLING
          </Title>
          <Content mb={2} mt={2}>
            Traditionally, this process is done by removing a very tiny piece of
            the metal to be tested. Today, sampling is usually performed in a
            non-invasive manner using an X-Ray Fluorescence Spectrometry
            machine, so that none of the metal is removed for testing.
          </Content>

          <Title color={"white"} mb={2} mt={3}>
            ASSAYING
          </Title>
          <Content mb={2} mt={2}>
            The process of testing the jewellery to determine whether the
            percentage of precious metal within the alloy meets the required
            standard.
          </Content>

          <Title color={"white"} mb={2} mt={3}>
            HALLMARKING
          </Title>
          <Content mb={2} mt={2}>
            The process of applying the appropriate marks to the item tested.
          </Content>
        </Box>
      </LeftBar>
      <Box
        flex={{ _: 1, tablet: "2 1 auto" }}
        width={{ _: "100%", tablet: "70%" }}
      >
        <RightBar py={4}>
          <Title mb={2}>HALLMARKS - WHAT TO LOOK FOR</Title>
          <picture>
            <source
              media="(max-width: 767px)"
              sizes="100vw"
              srcSet={`
            ${require("images/info/hallmark/mobile/image2_mobile.jpg")} 767w
          `}
            />

            <source
              media="(min-width: 768px)"
              sizes="100vw"
              srcSet={`
            ${require("images/info/hallmark/image2_desktop.jpg")} 1045w,
            ${require("images/info/hallmark/2x/image2_desktop.jpg")} 2090w
          `}
            />
            <Box
              as="img"
              sizes="100vw"
              src={require("images/info/hallmark/mobile/image2_mobile.jpg")}
              width={{ _: "70%", tablet: "50%" }}
            />
          </picture>
          <Box mx={4}>
            <Content mb={2} mt={2}>
              The hallmarks will be stamped on an inconspicuous location on all
              {sl.config.site_title} jewellery, and will not be visible when
              worn. The hallmark consists of at least three compulsory parts:
            </Content>
            <Content mb={2} mt={2}>
              <BoldContent>The Sponsor&apos;s Mark -</BoldContent>
              This mark is a unique mark identifying the maker or sponsor who
              has sent the items to be hallmarked.
            </Content>
            <Content mb={2} mt={2}>
              <BoldContent>The Fineness Mark - </BoldContent>
              denotes the fineness of the metal. The purity of the precious
              metal content is recorded in parts per 1000. For example, 14 carat
              gold jewellery must contain 585 parts per 1000 of pure gold. Gold
              jewellery meeting this standard will be marked with 585 to denote
              14 carat.
            </Content>
            <Content mb={2} mt={2}>
              <BoldContent> The Assay Office Mark - </BoldContent>
              indicates which UK Assay Office tested and marked the item.
            </Content>
            <picture>
              <source
                media="(max-width: 767px)"
                sizes="100vw"
                srcSet={`
            ${require("images/info/hallmark/mobile/image3_mobile.jpg")} 767w
          `}
              />

              <source
                media="(min-width: 768px)"
                sizes="60vw"
                srcSet={`
            ${require("images/info/hallmark/image3_desktop.jpg")} 1045w,
            ${require("images/info/hallmark/2x/image3_desktop.jpg")} 2090w
          `}
              />
              <Box
                as="img"
                sizes="60vw"
                src={require("images/info/hallmark/image3_desktop.jpg")}
              />
            </picture>
            <SmallContent mb={2} mt={2}>
              <SmallBoldContent>LEFT TO RIGHT: </SmallBoldContent>
              Our Sponsors mark, the mark of 14ct gold and The Assay Office Mark
              of Edinburgh.
            </SmallContent>
          </Box>
        </RightBar>
      </Box>
    </Section>
    <Box mx={6} my={4} textAlign="center">
      <picture>
        <source
          media="(max-width: 767px)"
          sizes="100vw"
          srcSet={`
            ${require("images/info/hallmark/mobile/jewlr_diamond.jpg")} 767w
          `}
        />

        <source
          media="(min-width: 768px)"
          sizes="100vw"
          srcSet={`
            ${require("images/info/hallmark/jewlr_diamond.jpg")} 1045w,
            ${require("images/info/hallmark/2x/jewlr_diamond.jpg")} 2090w
          `}
        />
        <Box
          as="img"
          mb={1}
          src={require("images/info/hallmark/jewlr_diamond.jpg")}
        />
      </picture>
      <BoldTitle>
        IT IS IMPORTANT TO US AT {sl.config.site_title.toUpperCase()} THAT OUR
        JEWELLERY REMAINS ONLY IN THE MOST CAPABLE HANDS. AS SUCH, WE HAVE OUR
        JEWELLERY HALLMARKED AT THE RENOWNED AND RESPECTED ASSAY OFFICE IN
        EDINBURGH.
      </BoldTitle>
    </Box>

    <ContentBorder mt={2} />

    <Section mt={2}>
      <Box
        flex={{ _: 1, tablet: "2 1 auto" }}
        width={{ _: "100%", tablet: "30%" }}
      >
        <picture>
          <source
            media="(max-width: 767px)"
            sizes="100vw"
            srcSet={`
            ${require("images/info/hallmark/mobile/image4_mobile.jpg")} 767w
          `}
          />

          <source
            media="(min-width: 768px)"
            sizes="100vw"
            srcSet={`
            ${require("images/info/hallmark/image4_tablet.jpg")} 1045w,
            ${require("images/info/hallmark/2x/image4_desktop.jpg")} 2090w
          `}
          />
          <Box
            as="img"
            height="100%"
            src={require("images/info/hallmark/image4_tablet.jpg")}
            width="100%"
          />
        </picture>
      </Box>
      <Box
        flex={{ _: 1, tablet: "2 1 auto" }}
        width={{ _: "100%", tablet: "70%" }}
      >
        <ContentBox mx={4} py={4} textAlign="center">
          <Title mb={2}>QUALITY SERVICE, GUARANTEED</Title>
          <Content>
            It is important to us at {sl.config.site_title} that our jewellery
            remains only in the most capable hands. As such, we have our
            jewellery hallmarked at the renowned and respected Assay Office in
            Edinburgh. This office has been in operation since 1457, carefully
            testing precious metals and winning UK industry awards for their
            customer service and superior workmanship. Please visit the{" "}
            <Link href="https://www.edinburghassayoffice.co.uk/">
              Edinburgh Assay Office
            </Link>{" "}
            to learn more about their commitment to customer satisfaction.
          </Content>
          <Content pt={1}>
            {sl.config.site_title} is a member of the Assay Assured scheme which
            was developed to protect consumers when purchasing jewellery online.
            All retailers signed up to the scheme are subject to website audits
            and agree that any problems with precious metals purchased will be
            independently resolved at no charge to the customer. Find out more
            about Assay Assured.{" "}
            <Link href="https://www.assayassured.co.uk/">Learn More</Link>
          </Content>
        </ContentBox>
      </Box>
    </Section>
    <Box mt={4}>
      <Perks />
    </Box>
  </Box>
)

export default Hallmark
